/**
 * Coordinate conversions used by the generic map control.
 *
 * The map always works in WGS84 (EPSG:4326). Model properties may be stored in another system, which
 * is declared per control instance through the CoordinateSystem setting - the control converts on read
 * and on write, so a form can bind the map straight to existing national grid columns.
 *
 * Supported systems:
 *  - Wgs84  : latitude/longitude in decimal degrees, no conversion.
 *  - D96Tm  : Slovenian national grid D96/TM (EPSG:3794). Transverse Mercator on GRS80, based on
 *             ETRS89, so no datum shift towards WGS84 is applied (the residual is well below a metre,
 *             far under the precision this control is used at).
 */

export type GeoCoordinateSystem = 'Wgs84' | 'D96Tm';

export interface LatLngPair {
  lat: number;
  lng: number;
}

/** Projection parameters of D96/TM (EPSG:3794). */
const D96TM = {
  /** GRS80 semi-major axis in meters. */
  a: 6378137.0,
  /** GRS80 inverse flattening. */
  invF: 298.257222101,
  /** Scale factor at the central meridian. */
  k0: 0.9999,
  /** Longitude of the central meridian, in degrees. */
  lon0: 15.0,
  /** Latitude of origin, in degrees. */
  lat0: 0.0,
  /** False easting in meters. */
  falseEasting: 500000.0,
  /** False northing in meters. */
  falseNorthing: -5000000.0
};

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/** Mean earth radius in meters, used for the haversine distance. */
const EARTH_RADIUS = 6371008.8;

/**
 * Converts a coordinate pair stored in the given system into WGS84 latitude/longitude.
 * Returns null when the input is not a usable pair of numbers.
 */
export function toWgs84(
  first: any,
  second: any,
  system: GeoCoordinateSystem
): LatLngPair | null {
  const a = toNumber(first);
  const b = toNumber(second);

  if (a === null || b === null) {
    return null;
  }

  if (system === 'D96Tm') {
    return d96TmToWgs84(a, b);
  }

  return { lat: a, lng: b };
}

/**
 * Converts WGS84 latitude/longitude into the given system. The result keeps the same property order
 * as toWgs84: `lat` is what belongs in the latitude binding, `lng` what belongs in the longitude one.
 */
export function fromWgs84(
  lat: number,
  lng: number,
  system: GeoCoordinateSystem
): LatLngPair {
  if (system === 'D96Tm') {
    return wgs84ToD96Tm(lat, lng);
  }

  return { lat: lat, lng: lng };
}

/**
 * Inverse transverse Mercator: D96/TM northing/easting -> WGS84 latitude/longitude.
 * @param northing D96/TM northing (N, often stored as X)
 * @param easting D96/TM easting (E, often stored as Y)
 */
export function d96TmToWgs84(northing: number, easting: number): LatLngPair {
  const f = 1 / D96TM.invF;
  const a = D96TM.a;
  const e2 = f * (2 - f);
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const ep2 = e2 / (1 - e2);

  const x = easting - D96TM.falseEasting;
  const y = northing - D96TM.falseNorthing;

  const m0 = meridionalArc(D96TM.lat0 * DEG_TO_RAD, a, e2);
  const m = m0 + y / D96TM.k0;
  const mu = m / (a * (1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 * e2 * e2) / 256));

  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * Math.pow(e1, 3)) / 32) * Math.sin(2 * mu) +
    ((21 * e1 * e1) / 16 - (55 * Math.pow(e1, 4)) / 32) * Math.sin(4 * mu) +
    ((151 * Math.pow(e1, 3)) / 96) * Math.sin(6 * mu) +
    ((1097 * Math.pow(e1, 4)) / 512) * Math.sin(8 * mu);

  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = Math.tan(phi1);

  const c1 = ep2 * cosPhi1 * cosPhi1;
  const t1 = tanPhi1 * tanPhi1;
  const n1 = a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1);
  const r1 = (a * (1 - e2)) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5);
  const d = x / (n1 * D96TM.k0);

  const d2 = d * d;
  const d4 = d2 * d2;
  const d6 = d4 * d2;

  const lat =
    phi1 -
    ((n1 * tanPhi1) / r1) *
      (d2 / 2 -
        ((5 + 3 * t1 + 10 * c1 - 4 * c1 * c1 - 9 * ep2) * d4) / 24 +
        ((61 + 90 * t1 + 298 * c1 + 45 * t1 * t1 - 252 * ep2 - 3 * c1 * c1) * d6) / 720);

  const lng =
    (d -
      ((1 + 2 * t1 + c1) * d2 * d) / 6 +
      ((5 - 2 * c1 + 28 * t1 - 3 * c1 * c1 + 8 * ep2 + 24 * t1 * t1) * d4 * d) / 120) /
    cosPhi1;

  return {
    lat: lat * RAD_TO_DEG,
    lng: D96TM.lon0 + lng * RAD_TO_DEG
  };
}

/**
 * Forward transverse Mercator: WGS84 latitude/longitude -> D96/TM.
 * `lat` of the result carries the northing, `lng` the easting.
 */
export function wgs84ToD96Tm(latDegrees: number, lngDegrees: number): LatLngPair {
  const f = 1 / D96TM.invF;
  const a = D96TM.a;
  const e2 = f * (2 - f);
  const ep2 = e2 / (1 - e2);

  const phi = latDegrees * DEG_TO_RAD;
  const lambda = lngDegrees * DEG_TO_RAD;
  const lambda0 = D96TM.lon0 * DEG_TO_RAD;

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = Math.tan(phi);

  const n = a / Math.sqrt(1 - e2 * sinPhi * sinPhi);
  const t = tanPhi * tanPhi;
  const c = ep2 * cosPhi * cosPhi;
  const aa = (lambda - lambda0) * cosPhi;

  const aa2 = aa * aa;
  const aa3 = aa2 * aa;
  const aa4 = aa2 * aa2;
  const aa5 = aa4 * aa;
  const aa6 = aa4 * aa2;

  const m = meridionalArc(phi, a, e2);
  const m0 = meridionalArc(D96TM.lat0 * DEG_TO_RAD, a, e2);

  const easting =
    D96TM.falseEasting +
    D96TM.k0 *
      n *
      (aa +
        ((1 - t + c) * aa3) / 6 +
        ((5 - 18 * t + t * t + 72 * c - 58 * ep2) * aa5) / 120);

  const northing =
    D96TM.falseNorthing +
    D96TM.k0 *
      (m -
        m0 +
        n *
          tanPhi *
          (aa2 / 2 +
            ((5 - t + 9 * c + 4 * c * c) * aa4) / 24 +
            ((61 - 58 * t + t * t + 600 * c - 330 * ep2) * aa6) / 720));

  return { lat: northing, lng: easting };
}

/** Distance between two WGS84 points in meters (haversine). */
export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLng = (lng2 - lng1) * DEG_TO_RAD;

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * DEG_TO_RAD) *
      Math.cos(lat2 * DEG_TO_RAD) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Area of a WGS84 ring in square meters, using the spherical excess of the polygon.
 * `ring` is a list of [lng, lat] pairs as they appear in GeoJSON.
 */
export function ringAreaSquareMeters(ring: number[][]): number {
  if (!ring || ring.length < 3) {
    return 0;
  }

  let total = 0;

  for (let i = 0; i < ring.length; i++) {
    const p1 = ring[i];
    const p2 = ring[(i + 1) % ring.length];

    if (!isPosition(p1) || !isPosition(p2)) {
      return 0;
    }

    total +=
      (p2[0] - p1[0]) *
      DEG_TO_RAD *
      (2 + Math.sin(p1[1] * DEG_TO_RAD) + Math.sin(p2[1] * DEG_TO_RAD));
  }

  return Math.abs((total * EARTH_RADIUS * EARTH_RADIUS) / 2);
}

/** Meridional arc length from the equator to the given latitude. */
function meridionalArc(phi: number, a: number, e2: number): number {
  return (
    a *
    ((1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 * e2 * e2) / 256) * phi -
      ((3 * e2) / 8 + (3 * e2 * e2) / 32 + (45 * e2 * e2 * e2) / 1024) * Math.sin(2 * phi) +
      ((15 * e2 * e2) / 256 + (45 * e2 * e2 * e2) / 1024) * Math.sin(4 * phi) -
      ((35 * e2 * e2 * e2) / 3072) * Math.sin(6 * phi))
  );
}

function isPosition(value: any): boolean {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    isFinite(value[0]) &&
    isFinite(value[1])
  );
}

/**
 * Model values arrive as numbers, as strings (also with a decimal comma) or as null. Anything that is
 * not a finite number is treated as "no value".
 */
export function toNumber(value: any): number | null {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return isFinite(value) ? value : null;
  }

  const parsed = parseFloat(String(value).replace(',', '.'));

  return isFinite(parsed) ? parsed : null;
}
